import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, Trash2, Download, Hotel as HotelIcon, Loader2 } from "lucide-react";

export default function Hotels() {
  const [list, setList] = useState([]);
  const [source, setSource] = useState("Agoda");
  const [uploading, setUploading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/hotel-bookings");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/hotel-bookings/upload-pdf?source=${encodeURIComponent(source)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Booking hotel ${data.hotel_booking.booking_number} berhasil dibuat`);
      if (data.duplicate) {
        toast.warning(`Perhatian: konfirmasi ${data.duplicate.confirmation_number} sudah pernah diproses untuk ${data.duplicate.guest_name}`, { duration: 6000 });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload/parse hotel PDF");
    } finally {
      setUploading(false);
    }
  };

  const del = async (h) => {
    if (!window.confirm(`Hapus booking ${h.booking_number}?`)) return;
    await api.delete(`/hotel-bookings/${h.id}`);
    toast.success("Dihapus");
    load();
  };

  const tok = () => localStorage.getItem("gg_token");
  const originalUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/hotel-bookings/${id}/original.pdf?token=${tok()}`;

  const rupiah = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-6" data-testid="hotels-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Hotel Bookings</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Upload PDF booking hotel dari Agoda / Booking.com / Traveloka — AI ekstrak otomatis.</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sumber</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="mt-1" data-testid="hotel-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Agoda","Booking.com","Traveloka","Tiket.com","Trip.com","Hotel Direct","Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">PDF booking</label>
              <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-[#c9d4a5] rounded-lg py-3 cursor-pointer hover:bg-[#fafcf3] transition" data-testid="hotel-upload-label">
                {uploading ? <Loader2 size={18} className="animate-spin text-[#556b2f]"/> : <UploadCloud size={18} className="text-[#556b2f]"/>}
                <span className="text-sm font-medium text-slate-700">{uploading ? "Memproses..." : "Klik untuk pilih PDF"}</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="hotel-pdf-upload"/>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card */}
      <div className="md:hidden space-y-3">
        {list.map((h) => (
          <div key={h.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`hotel-card-${h.booking_number}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="mono font-bold text-slate-900 text-sm">{h.booking_number}</div>
                <div className="font-semibold truncate">{h.hotel_name}</div>
                <div className="text-xs text-slate-500 truncate">{h.city}, {h.country} · {h.booking_source}</div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div><div className="text-slate-400">Guest</div><div className="font-semibold">{h.guest_name || "-"}</div></div>
              <div><div className="text-slate-400">Room</div><div className="font-semibold">{h.room_type || "-"}</div></div>
              <div><div className="text-slate-400">Check-in</div><div className="mono">{h.check_in || "-"}</div></div>
              <div><div className="text-slate-400">Check-out</div><div className="mono">{h.check_out || "-"}</div></div>
            </div>
            <div className="mt-2 flex justify-between items-center">
              <div className="mono font-bold text-[#556b2f]">{h.total_price ? rupiah(h.total_price) : "-"}</div>
              <div className="flex gap-1">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/hotel-bookings/${h.id}/voucher.pdf?token=${tok()}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Gogojalan Voucher"><HotelIcon size={14} className="text-[#556b2f]"/></Button></a>
                <a href={originalUrl(h.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><Download size={14}/></Button></a>
                <Button size="sm" variant="ghost" onClick={() => del(h)}><Trash2 size={14} className="text-red-600"/></Button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-center text-slate-400 py-10 border border-dashed rounded-lg">Belum ada booking hotel. Upload PDF di atas untuk mulai.</div>}
      </div>

      {/* Desktop grid */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-4">
        {list.map((h) => (
          <Card key={h.id} data-testid={`hotel-row-${h.booking_number}`}>
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f0f2e6] text-[#556b2f] flex items-center justify-center shrink-0"><HotelIcon size={18}/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="mono text-xs text-slate-500">{h.booking_number} · {h.booking_source}</div>
                    <div className="flex gap-1">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/hotel-bookings/${h.id}/voucher.pdf?token=${tok()}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Gogojalan Voucher"><HotelIcon size={14} className="text-[#556b2f]"/></Button></a>
                      <a href={originalUrl(h.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="PDF Asli"><Download size={14}/></Button></a>
                      <Button size="sm" variant="ghost" onClick={() => del(h)}><Trash2 size={14} className="text-red-600"/></Button>
                    </div>
                  </div>
                  <div className="font-bold text-slate-900">{h.hotel_name || "-"}</div>
                  <div className="text-xs text-slate-500 truncate">{h.hotel_address}</div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <div><div className="text-xs text-slate-400">Guest</div><div className="font-medium truncate">{h.guest_name}</div></div>
                    <div><div className="text-xs text-slate-400">Room</div><div className="font-medium truncate">{h.room_type} × {h.room_qty || 1}</div></div>
                    <div><div className="text-xs text-slate-400">Confirmation</div><div className="mono truncate">{h.confirmation_number}</div></div>
                    <div><div className="text-xs text-slate-400">Check-in</div><div className="mono">{h.check_in}</div></div>
                    <div><div className="text-xs text-slate-400">Check-out</div><div className="mono">{h.check_out}</div></div>
                    <div><div className="text-xs text-slate-400">Total</div><div className="mono font-bold text-[#556b2f]">{h.total_price ? rupiah(h.total_price) : "-"}</div></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {list.length === 0 && <div className="col-span-full text-center text-slate-400 py-10 border border-dashed rounded-lg">Belum ada booking hotel. Upload PDF di atas untuk mulai.</div>}
      </div>
    </div>
  );
}
